import { AdTypeOption, AdTypeOptions } from '@/_shared/types'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs'
import { Loader } from '@/components/custom-components/Loader'
import { ErrorDisplay } from '@/components/error.tsx'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { LandingAdCard } from '@/components/templates/LandingAdCard'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

const LIMIT = 20
type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/saved/ad-lander-templates')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const navigate = useNavigate()

  const {
    data: savedAdLanderTemplatesData,
    isLoading: loadingLanderTemplates,
    isError: landerTemplatesError,
    fetchNextPage: fetchNextPageTemplates,
    hasNextPage: hasNextPageTemplates,
    isFetchingNextPage: isFetchingNextPageTemplates,
  } = trpc.getAllSavedLandingPageTemplatesPaginated.useInfiniteQuery(
    { limit: LIMIT },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { ref, inView } = useInView({
    threshold: 0.5,
  })

  useEffect(() => {
    if (inView) {
      if (
        hasNextPageTemplates &&
        !isFetchingNextPageTemplates &&
        (savedAdLanderTemplatesData?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT
      ) {
        fetchNextPageTemplates()
      }
    }
  }, [
    inView,
    hasNextPageTemplates,
    isFetchingNextPageTemplates,
    fetchNextPageTemplates,
    savedAdLanderTemplatesData?.pages,
  ])

  if (landerTemplatesError) {
    return (
      <div className="px-10">
        <ErrorDisplay />
      </div>
    )
  }

  if (loadingLanderTemplates) {
    return (
      <div className="flex justify-center items-center w-full h-screen m-auto">
        <Loader />
      </div>
    )
  }

  const savedAdLanderTemplates = savedAdLanderTemplatesData?.pages.flatMap((page) => page.results) ?? []

  const isEmpty = savedAdLanderTemplates.length === 0

  return (
    <Stack className="gap-3 lg:gap-8">
      <Stack className="gap-3 lg:gap-8">
        <div className="flex justify-between gap-2">
          <div>
            <Text weight="semibold" size="xxl">
              All Saved Lander Templates
            </Text>
          </div>
          <div className="flex items-center gap-2 justify-end flex-wrap">
            <FeatureTabs
              tabItems={[
                { name: 'Ads', link: '/feeds/saved/ads' },
                { name: 'Landers', link: '/feeds/saved/ad-lander-templates' },
                { name: 'Emails', link: '/feeds/saved/emails' },
              ]}
            />

            <Select
              value={AdTypeOptions.ONLY_TEMPLATES}
              onValueChange={(value: AdTypeOption) => {
                if (value === AdTypeOptions.ALL_SAVED) {
                  navigate({
                    to: `/feeds/saved/ad-landers`,
                  })
                }
              }}
            >
              <SelectTrigger className="w-33 h-8 max-h-8">
                <SelectValue placeholder="Ad Type" className="rounded-md" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(AdTypeOptions).map((item) => (
                    <SelectItem key={item} value={item} className="py-1.5">
                      {item}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Stack>

      <div>
        {isEmpty ? (
          <div className="h-[80vh] flex justify-center items-center">
            <p className="text-center">You do not have any ad lander inspiration saved</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {savedAdLanderTemplates.map((card) => (
              <LandingAdCard
                key={card['LP ID']}
                adData={{
                  Screenshot: card['Landing Page Screenshot'],
                  ID: card['LP ID'],
                  created: card.Created,
                  category: card.Category,
                  isSaved: card.isSaved,
                  imageUrl: card.imageUrl,
                }}
                showTemplateButton
                type="landing-page"
              />
            ))}
          </div>
        )}
        {hasNextPageTemplates && (
          <div ref={ref} className="flex justify-center py-4">
            {isFetchingNextPageTemplates ? <Loader /> : null}
          </div>
        )}
      </div>
    </Stack>
  )
}
